// i18n — Thailand expansion, THAILAND.md §11 Phase 1.
//
// Language is NOT user-selectable and NOT browser-detected: it follows the
// country gate (§11.0). Thai renders only when `$isTH` is true, i.e. when
// settings.country = 'TH'. Every other deployment — including Singapore
// production — renders `en`.
//
// `en.json` holds the exact strings that were previously hardcoded in the
// templates, so a gate-closed render is character-for-character what it was
// before the extraction.
//
// Usage in components:  {{ $t('home.hero_title') }}
// A missing Thai key falls back to English (fallbackLocale), never to a blank.

import Vue from "vue";
import VueI18n from "vue-i18n";
import moment from "moment";
import en from "../locales/en.json";
import th from "../locales/th.json";
import { profileFor, DEFAULT_COUNTRY } from "../config/countries";

// Month and weekday names (`MMM`, `dddd`) and relative times (`fromNow()`) are
// rendered by moment, not by vue-i18n — ~173 component files format dates this
// way, so they cannot be covered by string extraction. vue.config.js strips
// moment's ~140 bundled locales, but that only blocks moment's own dynamic
// require; this explicit import still resolves, adding just the one locale.
//
// Importing a locale makes it the ACTIVE one as a side effect (moment's
// defineLocale switches to whatever it just defined), so English is restored
// immediately. applyLocaleFromGate() below is the only place that may change it.
import "moment/locale/th";
moment.locale("en");

Vue.use(VueI18n);

const i18n = new VueI18n({
  locale: "en",
  fallbackLocale: "en",
  messages: { en, th },
  // Missing keys are expected while extraction is in progress (only part of the
  // site is translated). Warnings are silenced so the console stays usable;
  // fallbackLocale still fills in the English text.
  silentFallbackWarn: true,
  silentTranslationWarn: true,
});

// Called once after the country gate resolves, before the app mounts.
// Accepts the resolved gate state ({ country, isTH }); a bare boolean is still
// honoured (legacy callers) and means TH. Anything falsy/malformed → SG/en.
export function applyLocaleFromGate(gate) {
  const open = gate === true || (gate && gate.isTH === true);
  const country = open ? (gate && gate.country) || "TH" : DEFAULT_COUNTRY;
  i18n.locale = profileFor(country).locale;
  // Keep moment in step so dates read in the same language as the page. Gate
  // closed means 'en', i.e. exactly the formatting SG has always produced.
  moment.locale(i18n.locale);
  // Let CSS target Thai text (font stack / line-height fixes) and tell
  // screen readers + search engines which language the page is in.
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("lang", i18n.locale);
  }
  return i18n.locale;
}

export default i18n;
